import React from "react";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import playingMusic from "../assets/lottie-files/playingMusic.lottie";
import { Row, Col } from "react-bootstrap";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.75rem",
      height: "100%",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2
      style={{
        fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: 56,
        height: 4,
        background: "linear-gradient(90deg, #6dc744, #4a8c25)",
        borderRadius: 2,
        marginTop: 8,
      }}
    />
  </div>
);

const BecomeBusker: React.FC = () => {
  return (
    <div>
      {/* ── HERO (dark, for contrast) ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span
              variants={fadeUp}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}
            >
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Open Calls
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}
            >
              Perform at the{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                SJFM
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}
            >
              Share your talent and be part of our vibrant community events! Whether you're a musician, artist, or entertainer, there's a stage waiting for you at St. John's Farmers' Market.
            </motion.p>

            <motion.div variants={fadeUp}>
              <a href="mailto:eventsmanager@sjfm.ca" style={{ textDecoration: "none" }}>
                <button
                  style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", border: "none", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
                >
                  Get in Touch →
                </button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}
          >
            <DotLottieReact src={playingMusic} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* ── PERFORMANCE OPTIONS ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>🎶 Performance Options</SectionHeading></motion.div>

            <Row xs={1} md={2} className="g-4">
              <Col>
                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                  <LightCard style={{ textAlign: "center", borderTop: "4px solid #4a8c25", background: "#f2fbee", borderColor: "rgba(45,106,20,0.15)" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🎵</div>
                    <h3 style={{ color: "#2d6a14", fontWeight: 700, fontSize: "1.25rem", marginBottom: "1rem" }}>
                      Market Café (Indoor Sets)
                    </h3>
                    <p style={{ color: "#4a5a40", lineHeight: 1.7, margin: 0, fontSize: "1.05rem" }}>
                      We invite one performer or group each Market day to play a set in our Market Café. These are typically musical acts. While these performances are currently unpaid (we're working on changing that!), performers are welcome to pass a hat or collect donations.
                    </p>
                  </LightCard>
                </motion.div>
              </Col>
              <Col>
                <motion.div variants={fadeUp} style={{ height: "100%" }}>
                  <LightCard style={{ textAlign: "center", borderTop: "4px solid #c6252d", background: "#fdecea", borderColor: "rgba(198,37,45,0.12)" }}>
                    <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🌳</div>
                    <h3 style={{ color: "#a01e25", fontWeight: 700, fontSize: "1.25rem", marginBottom: "1rem" }}>
                      Outdoor Busking
                    </h3>
                    <p style={{ color: "#4a5a40", lineHeight: 1.7, margin: 0, fontSize: "1.05rem" }}>
                      From June to November, we utilize our outdoor spaces and welcome a variety of performance styles. After reviewing your application, we may request performance samples or an audition to ensure alignment with our Market atmosphere.
                    </p>
                  </LightCard>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── SELECTION CRITERIA ── */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>🎯 Selection Criteria</SectionHeading></motion.div>

            <motion.div variants={fadeUp}>
              <LightCard style={{ borderLeft: "4px solid #6dc744", borderColor: "rgba(45,106,20,0.15)", display: "flex", alignItems: "flex-start", gap: "1.5rem", flexWrap: "wrap" }}>
                <div style={{ fontSize: "3.5rem", lineHeight: 1, flexShrink: 0 }}>🎯</div>
                <p style={{ color: "#3a4a35", fontSize: "1.1rem", lineHeight: 1.72, margin: 0 }}>
                  Performers are selected based on the quality of their act, compatibility with the Market's vibe, and the potential benefit to the artist. For us, a{" "}
                  <b style={{ color: "#2d6a14" }}>busker</b> is someone who entertains without selling physical products.
                </p>
              </LightCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── PRODUCT-BASED PERFORMERS ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>🎨 Product-Based Performers</SectionHeading></motion.div>

            <motion.div variants={fadeUp}>
              <LightCard style={{ borderLeft: "4px solid #C19A3B", background: "#fdf6e3", borderColor: "rgba(193,154,59,0.2)", display: "flex", alignItems: "flex-start", gap: "1.5rem", flexWrap: "wrap" }}>
                <div style={{ fontSize: "3.5rem", lineHeight: 1, flexShrink: 0 }}>🎨</div>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <p style={{ color: "#3a4a35", fontSize: "1.1rem", lineHeight: 1.72, marginBottom: "1.5rem" }}>
                    If you offer products or services for a donation or fee (e.g., face painting, balloon art), please reach out directly to our Events Manager to coordinate logistics.
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                    <span style={{ color: "#7a8a70", fontSize: "0.95rem" }}>Contact:</span>
                    <a href="mailto:eventsmanager@sjfm.ca" style={{ textDecoration: "none" }}>
                      <button
                        style={{ background: "linear-gradient(135deg, #C19A3B, #8a6d28)", color: "white", border: "none", borderRadius: 999, padding: "0.55rem 1.4rem", fontWeight: 700, cursor: "pointer", fontSize: "0.92rem" }}
                      >
                        eventsmanager@sjfm.ca
                      </button>
                    </a>
                  </div>
                </div>
              </LightCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── HOW TO APPLY ── */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}><SectionHeading>📝 How to Apply</SectionHeading></motion.div>

            <Row xs={1} md={3} className="g-4">
              {[
                { n: 1, icon: "📝", color: "#4a8c25", bg: "#f2fbee", title: "Fill Out the Application", desc: "Submit the Busker Application form available on our website. Indicate which type of performance you're applying for." },
                { n: 2, icon: "👀", color: "#C19A3B", bg: "#fdf6e3", title: "Application Review", desc: "Our team will assess your submission. If selected, you may be asked to provide performance samples or complete an audition." },
                { n: 3, icon: "📅", color: "#c6252d", bg: "#fdecea", title: "Scheduling & Confirmation", desc: "Selected performers will receive confirmation and scheduling details. Performance slots are limited and subject to availability." },
              ].map((step) => (
                <Col key={step.n}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: step.bg, borderTop: `4px solid ${step.color}`, borderColor: `${step.color}22`, paddingTop: "2.5rem", position: "relative" }}>
                      <div style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", width: 30, height: 30, background: step.color, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.82rem", color: "white" }}>
                        {step.n}
                      </div>
                      <div style={{ fontSize: "2.8rem", marginBottom: "0.75rem" }}>{step.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.6rem" }}>{step.title}</h4>
                      <p style={{ color: "#5a6a50", margin: 0, fontSize: "0.92rem", lineHeight: 1.65 }}>{step.desc}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>

            <motion.p variants={fadeUp} style={{ color: "#9aaa90", marginTop: "2rem", fontSize: "0.9rem", textAlign: "center" }}>
              <b style={{ color: "#5a6a50" }}>Note:</b> Not all applicants can be accommodated immediately. We appreciate your patience and interest in performing at the SJFM!
            </motion.p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BecomeBusker;
